/**
 * SettingsApi over fetch (contracts routes: profile, handles, settings, email domains, members, invites, account) with the session's
 * access token: refreshed before it expires, and once more after a 401. Writes with a clientId send it as the
 * Idempotency-Key.
 */
import type {
  AccountResponse,
  CalDavPasswordResponse,
  ListAppPasswordsResponse,
  MailProfileResponse,
  DataExportResponse,
  EmailDomainsResponse,
  ImportResponse,
  ImportTrashResponse,
  ImportUndoResponse,
  ListImportsResponse,
  SlackAppSettings,
  SlackSyncSettings,
  SlackConnectResponse,
  ListActivityResponse,
  MarkActivityReadResponse,
  NotificationSettingsResponse,
  NavigationSettingsResponse,
  AppearanceSettingsResponse,
  HandleAvailabilityResponse,
  InviteResponse,
  ListInvitesResponse,
  ListMembersResponse,
  ListSessionsResponse,
  MemberResponse,
  RevokeSessionsResponse,
  WorkspaceResponse,
} from "@bundle/contracts";
import { buildPath, routes, type RouteName } from "@bundle/contracts/routes";
import { ApiFailure, type ApiErrorKind, type SessionViewModel, type SettingsApi } from "@bundle/core";
import { API_BASE } from "../env.ts";

function kindFor(status: number): ApiErrorKind {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "notFound";
  if (status === 429) return "rateLimited";
  if (status >= 400 && status < 500) return "invalid";
  return status === 502 || status === 503 || status === 504 ? "network" : "server";
}

export function createSettingsApi(session: SessionViewModel, base: string = API_BASE): SettingsApi {
  async function call<T>(name: RouteName, params: Record<string, string>, body?: unknown, idempotencyKey?: string, query = ""): Promise<T> {
    const route = routes[name];
    const url = base + buildPath(route.path, params) + query;
    const send = async (token: string | undefined) => {
      const headers: Record<string, string> = { accept: "application/json" };
      if (body !== undefined) headers["content-type"] = "application/json";
      if (token !== undefined) headers.authorization = `Bearer ${token}`;
      if (idempotencyKey !== undefined) headers["idempotency-key"] = idempotencyKey;
      try {
        return await fetch(url, { method: route.method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
      } catch (e) {
        throw new ApiFailure("network", 0, "network", e instanceof Error ? e.message : "network error");
      }
    };
    await session.ensureFreshToken();
    let token = session.state.session?.token;
    let res = await send(token);
    if (res.status === 401 && token !== undefined && (await session.authRejected(token))) {
      token = session.state.session?.token;
      res = await send(token);
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let json: unknown;
    try {
      json = text === "" ? undefined : JSON.parse(text);
    } catch {
      json = undefined;
    }
    if (!res.ok) {
      const err = (json as { error?: { code?: string; message?: string } } | undefined)?.error;
      throw new ApiFailure(kindFor(res.status), res.status, err?.code ?? String(res.status), err?.message ?? res.statusText);
    }
    return json as T;
  }

  return {
    myProfile: async (ws) => (await call<MemberResponse>("myProfile", { ws })).member,
    updateProfile: async (ws, req, clientId) => (await call<MemberResponse>("updateProfile", { ws }, req, clientId)).member,
    checkHandle: (ws, handle) => call<HandleAvailabilityResponse>("checkHandle", { ws, handle }),
    workspace: async (ws) => (await call<WorkspaceResponse>("workspaceSettings", { ws })).workspace,
    updateWorkspace: async (ws, req) => (await call<WorkspaceResponse>("updateWorkspace", { ws }, req)).workspace,
    transferOwnership: async (ws, userId) => (await call<WorkspaceResponse>("transferOwnership", { ws }, { userId })).workspace,
    members: async (ws) => (await call<ListMembersResponse>("listMembers", { ws })).members,
    changeRole: async (ws, userId, req) => (await call<MemberResponse>("changeRole", { ws, userId }, req)).member,
    deactivate: async (ws, userId) => (await call<MemberResponse>("deactivateMember", { ws, userId }, {})).member,
    reactivate: async (ws, userId) => (await call<MemberResponse>("reactivateMember", { ws, userId }, {})).member,
    updateAccess: async (ws, userId, access) => (await call<MemberResponse>("updateAccess", { ws, userId }, { access })).member,
    invites: async (ws) => (await call<ListInvitesResponse>("listInvites", { ws })).invites,
    revokeInvite: (ws, inviteId) => call<void>("revokeInvite", { ws, inviteId }),
    resendInvite: async (ws, inviteId) => (await call<InviteResponse>("resendInvite", { ws, inviteId }, {})).invite,
    emailDomains: async (ws) => (await call<EmailDomainsResponse>("emailDomains", { ws })).domains,
    updateEmailDomains: async (ws, domains) => (await call<EmailDomainsResponse>("updateEmailDomains", { ws }, { domains })).domains,
    // ?all=1: every kind (mentions, threads, reactions, channel adds), not just joins.
    activity: async (ws) => (await call<ListActivityResponse>("listActivity", { ws }, undefined, undefined, "?all=1")).items,
    markActivityRead: async (ws, ids) => (await call<MarkActivityReadResponse>("markActivityRead", { ws }, ids === undefined ? { all: true } : { ids })).unread,
    notificationSettings: async () => (await call<NotificationSettingsResponse>("notificationSettings", {})).settings,
    updateNotificationSettings: async (settings) => (await call<NotificationSettingsResponse>("updateNotificationSettings", {}, settings)).settings,
    navigationSettings: async () => (await call<NavigationSettingsResponse>("navigationSettings", {})).settings,
    updateNavigationSettings: async (settings) => (await call<NavigationSettingsResponse>("updateNavigationSettings", {}, settings)).settings,
    appearanceSettings: async () => (await call<AppearanceSettingsResponse>("appearanceSettings", {})).settings,
    updateAppearanceSettings: async (settings) => (await call<AppearanceSettingsResponse>("updateAppearanceSettings", {}, settings)).settings,
    account: () => call<AccountResponse>("account", {}),
    sessions: async () => (await call<ListSessionsResponse>("accountSessions", {})).sessions,
    revokeOtherSessions: async () => (await call<RevokeSessionsResponse>("revokeOtherSessions", {}, {})).revoked,
    revokeSession: async (sessionId) => {
      await call<RevokeSessionsResponse>("revokeSession", { sessionId });
    },
    appPasswords: async (ws) => (await call<ListAppPasswordsResponse>("listAppPasswords", { ws })).appPasswords,
    createAppPassword: (ws, req) => call<CalDavPasswordResponse>("createAppPassword", { ws }, req),
    createAppleProfile: (ws, label) => call<MailProfileResponse>("createMailProfile", { ws }, { label }),
    revokeAppPassword: async (ws, appPasswordId) => {
      await call<unknown>("deleteAppPassword", { ws, appPasswordId });
    },
    revokeAllAppPasswords: async (ws) => {
      await call<unknown>("deleteCalDavPassword", { ws });
    },
    accountExport: () => call<DataExportResponse>("accountExport", {}),
    createAccountExport: () => call<DataExportResponse>("createAccountExport", {}, {}),
    workspaceExport: (ws) => call<DataExportResponse>("workspaceExport", { ws }),
    createWorkspaceExport: (ws) => call<DataExportResponse>("createWorkspaceExport", { ws }, {}),
    deleteAccount: async (req) => {
      await call<unknown>("deleteAccount", {}, req);
    },
    imports: async (ws) => (await call<ListImportsResponse>("listImports", { ws })).imports,
    createImport: async (ws, req) => (await call<ImportResponse>("createImport", { ws }, req)).import,
    importJob: async (ws, importId) => (await call<ImportResponse>("getImport", { ws, importId })).import,
    startImport: async (ws, importId, req) => (await call<ImportResponse>("startImport", { ws, importId }, req)).import,
    cancelImport: async (ws, importId) => (await call<ImportResponse>("cancelImport", { ws, importId }, {})).import,
    rescanImport: async (ws, importId) => (await call<ImportResponse>("rescanImport", { ws, importId }, {})).import,
    myImports: async (ws) => (await call<ListImportsResponse>("myImports", { ws })).imports,
    slackApp: (ws) => call<SlackAppSettings>("getSlackApp", { ws }),
    updateSlackApp: (ws, req) => call<SlackAppSettings>("updateSlackApp", { ws }, req),
    connectSlack: async (ws) => (await call<SlackConnectResponse>("connectSlack", { ws }, {})).url,
    slackSync: (ws) => call<SlackSyncSettings>("getSlackSync", { ws }),
    updateSlackSync: (ws, req) => call<SlackSyncSettings>("updateSlackSync", { ws }, req),
    runSlackSync: (ws) => call<SlackSyncSettings>("runSlackSync", { ws }, {}),
    stopSlackSync: (ws) => call<SlackSyncSettings>("stopSlackSync", { ws }, {}),
    importTrash: async (ws, importId) => (await call<ImportTrashResponse>("importTrash", { ws, importId })).trash,
    trashImport: async (ws, importId) => (await call<ImportUndoResponse>("trashImport", { ws, importId }, {})).undo,
    importDeletable: async (ws, importId) => (await call<ImportTrashResponse>("importDeletable", { ws, importId })).trash,
    deleteImport: async (ws, importId) => (await call<ImportUndoResponse>("deleteImport", { ws, importId }, {})).undo,
  };
}
